import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  canEditCompliance,
  canManageClients,
  type CreateTagInput,
  type TagOutput,
  type UpdateTagInput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { Tags } from 'lucide-react';
import { fmtDate } from '../lib/date.js';
import { useCapabilities } from '../hooks/useCapabilities.js';

type ModalMode = { kind: 'new' } | { kind: 'edit'; tag: TagOutput } | null;

export function TagsPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<TagOutput | null>(null);
  const [search, setSearch] = useState('');

  const { canDo } = useCapabilities();
  const canEdit = canDo('tags.manage');
  const canDelete = canDo('tags.delete');

  const list = useQuery({
    queryKey: ['tags', { search }],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '100' });
      if (search) p.set('search', search);
      const res = await fetch(`/v1/tags?${p.toString()}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TagOutput[] } };
      return body.data.items;
    },
  });

  // The tags endpoint returns the full set (no cursor/total), so sorting and
  // paging happen client-side with the same shared UI as the server-side tables.
  const TAG_PAGE_SIZE = 50;
  const { sortBy, sortOrder, pageIndex, toggleSort, goNext, goPrev } = useTableControls('name', 'asc', [search]);
  const activeFilters: ActiveFilter[] = [
    ...(search ? [{ key: 'search', label: `Search: ${search}` }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'search') setSearch('');
  }

  function clearAllFilters() {
    setSearch('');
  }
  const sorted = useMemo(() => {
    const arr = [...(list.data ?? [])];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'usageCount') cmp = a.usageCount - b.usageCount;
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [list.data, sortBy, sortOrder]);
  const pageItems = sorted.slice(pageIndex * TAG_PAGE_SIZE, (pageIndex + 1) * TAG_PAGE_SIZE);

  const create = useMutation({
    mutationFn: async (input: CreateTagInput) => {
      const res = await fetch('/v1/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setModal(null);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateTagInput }) => {
      const res = await fetch(`/v1/tags/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['entity-tags'] });
      setModal(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/tags/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['entity-tags'] });
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Tags</h1>
          <p className="text-sm text-muted">{list.data?.length ?? '—'} tags · used on clients, engagements and compliance items</p>
        </div>
        {canEdit && (
          <button onClick={() => setModal({ kind: 'new' })} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New tag</button>
        )}
      </header>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search tags…"
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <SortableTh label="Tag" sortKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium">Description</th>
              <SortableTh label="Usage" sortKey="usageCount" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-right" />
              <SortableTh label="Created" sortKey="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              {(canEdit || canDelete) && <th className="px-4 py-3 font-medium text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={5} />}
            {list.error && <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {!list.isLoading && !list.error && list.data?.length === 0 && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={5} onClearAll={clearAllFilters} />
                : <tr><td colSpan={5}><EmptyState icon={Tags} title="No tags yet." /></td></tr>
            )}
            {pageItems.map((t) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-medium" style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}1a` }}>
                    {t.name}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted">{t.description ?? '—'}</td>
                <td className="px-4 py-3 text-right text-white">{t.usageCount}</td>
                <td className="px-4 py-3 text-muted">{fmtDate(t.createdAt)}</td>
                {(canEdit || canDelete) && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {canEdit && <EditIcon onClick={() => setModal({ kind: 'edit', tag: t })} label="Edit tag" />}
                      {canDelete && <DeleteIcon onClick={() => setDeleting(t)} label="Delete tag" />}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={pageItems.length}
          limit={TAG_PAGE_SIZE}
          total={sorted.length}
          hasNext={(pageIndex + 1) * TAG_PAGE_SIZE < sorted.length}
          onPrev={goPrev}
          onNext={() => goNext(`page-${pageIndex + 1}`)}
        />
      </div>

      {modal && (
        <TagModal
          mode={modal}
          onClose={() => setModal(null)}
          onCreate={(input) => create.mutate(input)}
          onUpdate={(id, input) => update.mutate({ id, input })}
          isPending={create.isPending || update.isPending}
          error={(create.error?.message ?? update.error?.message) || null}
        />
      )}

      {deleting && (
        <DeleteModal
          tag={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          isPending={remove.isPending}
          error={remove.error?.message ?? null}
        />
      )}
    </div>
  );
}

function TagModal({
  mode,
  onClose,
  onCreate,
  onUpdate,
  isPending,
  error,
}: {
  mode: { kind: 'new' } | { kind: 'edit'; tag: TagOutput };
  onClose: () => void;
  onCreate: (input: CreateTagInput) => void;
  onUpdate: (id: string, input: UpdateTagInput) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = mode.kind === 'edit';
  const tag = isEdit ? mode.tag : null;
  const [form, setForm] = useState({
    name: tag?.name ?? '',
    color: tag?.color ?? '#34d399',
    description: tag?.description ?? '',
  });

  function updateForm<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input = {
      name: form.name.trim(),
      color: form.color,
      description: form.description.trim() || null,
    };
    if (isEdit) {
      onUpdate(tag!.id, input);
    } else {
      onCreate(input);
    }
  }

  return (
    <ModalShell title={isEdit ? 'Edit tag' : 'New tag'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            placeholder="lowercase-letters-digits-hyphens"
            maxLength={40}
            pattern="[a-z0-9-]+"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.color}
              onChange={(e) => updateForm('color', e.target.value)}
              className="w-10 h-9 bg-bg-base border border-border rounded-lg p-1"
            />
            <span className="text-sm text-muted font-mono">{form.color}</span>
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => updateForm('description', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            rows={2}
            maxLength={200}
          />
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteModal({ tag, onClose, onConfirm, isPending, error }: { tag: TagOutput; onClose: () => void; onConfirm: () => void; isPending: boolean; error: string | null }) {
  return (
    <ModalShell title="Delete tag" onClose={onClose}>
      <p className="text-sm text-white">Delete &ldquo;{tag.name}&rdquo;? It will be removed from {tag.usageCount} {tag.usageCount === 1 ? 'entity' : 'entities'}.</p>
      {error && <p className="text-xs text-rose-400 mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={isPending} className="bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">{isPending ? 'Deleting…' : 'Delete'}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

